/**
 * The audit log: who did what, to which record, and what came back.
 *
 * The filter dropdowns are built from `/api/v1/audit-log/facets`, which
 * reports the values actually present in the log with their counts. Listing
 * actions here instead would offer filters that match nothing and miss ones
 * that exist — the same drift the served-enums endpoint exists to stop, and
 * this log gains new action names whenever a handler does.
 *
 * Paging is by cursor, not offset: OFFSET grows expensive on ClickHouse as the
 * log grows, and this table is append-only so a cursor is stable in a way an
 * offset is not.
 */
import { useState } from 'react';
import {
  ActionIcon, Alert, Badge, Card, Code, Collapse, Group, Loader, Select, Stack,
  Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { IconChevronDown, IconSearch, IconX } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useAuditFacets, useAuditLog, type AuditFilters } from '../api/queries';
import { formatDateTime } from '../theme';

/** HTTP status classes, the one filter whose values are fixed by HTTP itself. */
const STATUS_CLASSES = [
  { value: '2xx', label: 'Success (2xx)' },
  { value: '4xx', label: 'Rejected (4xx)' },
  { value: '5xx', label: 'Failed (5xx)' },
];

function StatusBadge({ status }: { status: number }) {
  const colour =
    status >= 500 ? 'chartRed' : status >= 400 ? 'chartAmber' : status >= 200 ? 'chartGreen' : 'gray';
  return <Badge size="sm" variant="light" color={colour}>{status || '—'}</Badge>;
}

function LogRow({ entry }: { entry: NonNullable<ReturnType<typeof useAuditLog>['data']>['logs'][number] }) {
  const [open, setOpen] = useState(false);
  const hasDetails = entry.details != null;

  return (
    <>
      <Table.Tr
        style={{ cursor: hasDetails ? 'pointer' : 'default' }}
        onClick={() => hasDetails && setOpen((o) => !o)}
      >
        <Table.Td>
          <Text size="xs" c="dimmed">{formatDateTime(entry.created_at)}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" lineClamp={1}>{entry.user_email || '—'}</Text>
          <Text size="xs" c="dimmed">{entry.user_role}</Text>
        </Table.Td>
        <Table.Td><Text size="sm" fw={500}>{entry.action || '—'}</Text></Table.Td>
        <Table.Td>
          <Text size="sm">{entry.resource_type || '—'}</Text>
          {entry.resource_id && (
            <Text size="xs" c="dimmed" lineClamp={1}>{entry.resource_id}</Text>
          )}
        </Table.Td>
        <Table.Td>
          <Group gap={6} wrap="nowrap">
            <Badge size="xs" variant="outline" color="gray">{entry.http_method}</Badge>
            <Text size="xs" c="dimmed" lineClamp={1} maw={220}>{entry.http_path}</Text>
          </Group>
        </Table.Td>
        <Table.Td><StatusBadge status={entry.http_status} /></Table.Td>
        <Table.Td ta="right">
          <Text size="xs" c="dimmed">{entry.duration_ms ? `${entry.duration_ms}ms` : '—'}</Text>
        </Table.Td>
        <Table.Td w={34}>
          {hasDetails && (
            <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Show what changed">
              <IconChevronDown
                size={14}
                style={{ transform: open ? 'rotate(180deg)' : undefined }}
              />
            </ActionIcon>
          )}
        </Table.Td>
      </Table.Tr>
      {hasDetails && (
        <Table.Tr>
          <Table.Td colSpan={8} p={0} style={{ borderTop: 'none' }}>
            <Collapse in={open}>
              <Code block mx="sm" mb="sm">{JSON.stringify(entry.details, null, 2)}</Code>
            </Collapse>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

export function LogsPage() {
  const [filters, setFilters] = useState<AuditFilters>({});
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const facets = useAuditFacets();
  const { data, isPending, isFetching, error } = useAuditLog({ ...filters, q: debounced || null });

  const set = (patch: AuditFilters) => setFilters((f) => ({ ...f, ...patch, before: null }));
  const active = Object.entries(filters).filter(([k, v]) => k !== 'before' && v).length;

  const opts = (rows: { value: string; count: number }[] | undefined) =>
    (rows ?? [])
      .filter((r) => r.value)
      .map((r) => ({ value: r.value, label: `${r.value} (${r.count})` }));

  return (
    <Stack gap="sm">
      <Title order={2}>Logs</Title>

      <Card padding="sm">
        <Stack gap="xs">
          <Group gap="xs" align="flex-end">
            <TextInput
              placeholder="Search path, resource or user"
              leftSection={<IconSearch size={15} />}
              size="xs"
              styles={{ input: { fontSize: '0.8125rem' } }}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ width: 280 }}
            />
            {active > 0 && (
              <Tooltip label="Clear filters">
                <ActionIcon
                  variant="subtle"
                  size="md"
                  aria-label="Clear filters"
                  onClick={() => { setFilters({}); setSearch(''); }}
                >
                  <IconX size={15} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>

          <Group grow gap="xs" align="flex-start">
            {/* Every option here comes from the log itself, with its count. */}
            <Select
              size="xs"
              styles={{ input: { fontSize: '0.8125rem' } }}
              placeholder="All actions"
              searchable
              clearable
              data={opts(facets.data?.actions)}
              value={filters.action ?? null}
              onChange={(v) => set({ action: v })}
            />
            <Select
              size="xs"
              styles={{ input: { fontSize: '0.8125rem' } }}
              placeholder="All users"
              searchable
              clearable
              data={opts(facets.data?.users)}
              value={filters.user_email ?? null}
              onChange={(v) => set({ user_email: v })}
            />
            <Select
              size="xs"
              styles={{ input: { fontSize: '0.8125rem' } }}
              placeholder="All resources"
              searchable
              clearable
              data={opts(facets.data?.resource_types)}
              value={filters.resource_type ?? null}
              onChange={(v) => set({ resource_type: v })}
            />
            <Select
              size="xs"
              styles={{ input: { fontSize: '0.8125rem' } }}
              placeholder="All methods"
              clearable
              data={opts(facets.data?.methods)}
              value={filters.http_method ?? null}
              onChange={(v) => set({ http_method: v })}
            />
            <Select
              size="xs"
              styles={{ input: { fontSize: '0.8125rem' } }}
              placeholder="All outcomes"
              clearable
              data={STATUS_CLASSES}
              value={filters.status_class ?? null}
              onChange={(v) => set({ status_class: v })}
            />
          </Group>
        </Stack>
      </Card>

      {error && <Alert color="red" variant="light">{error.message}</Alert>}

      <Card p={0}>
        <Group justify="space-between" px="sm" py={6}>
          <Text size="sm">
            <Text span fw={700}>{(data?.total ?? 0).toLocaleString()}</Text>
            <Text span c="dimmed"> recorded actions</Text>
          </Text>
          {isFetching && <Loader size="xs" />}
        </Group>

        {isPending ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table.ScrollContainer minWidth={980}>
            <Table striped horizontalSpacing={6} verticalSpacing={7} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={150}>When</Table.Th>
                  <Table.Th>Who</Table.Th>
                  <Table.Th>Action</Table.Th>
                  <Table.Th>Resource</Table.Th>
                  <Table.Th>Request</Table.Th>
                  <Table.Th w={70}>Status</Table.Th>
                  <Table.Th w={70} ta="right">Took</Table.Th>
                  <Table.Th w={34} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(data?.logs ?? []).length === 0 && (
                  <Table.Tr><Table.Td colSpan={8}>
                    <Text ta="center" c="dimmed" py="lg">Nothing recorded for these filters.</Text>
                  </Table.Td></Table.Tr>
                )}
                {(data?.logs ?? []).map((entry) => (
                  <LogRow key={entry.id} entry={entry} />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
    </Stack>
  );
}
